<template>
  <v-container>
    <!-- Edit form above the table -->
    <div v-if="currentlyEditing" style="margin-bottom: 24px; padding: 16px; background: #fafafa; border-radius: 8px;">
      <div style="display: flex; flex-wrap: wrap; gap: 16px;">
        <div style="flex: 1 1 300px; min-width: 200px;">
          <v-text-field
            :model-value="getDateInputValue(currentlyEditing.sessiondate)"
            @update:model-value="val => updateSessionDate(val)"
            label="Session Date"
            type="date"
          />
          <v-select v-model="currentlyEditing.place" :items="placelist" label="Place" />
          <v-select v-model="currentlyEditing.game" :items="gameslist" label="Game" />
          <v-select v-model="currentlyEditing.bettype" :items="BET_TYPE_LIST_PICKER" label="Bet Type" />
        </div>
        <div style="flex: 1 1 300px; min-width: 200px;">
          <v-text-field v-if="currentlyEditing.type === CASH" v-model="currentlyEditing.net" label="Net" type="number" step=".01" />
          <v-text-field v-if="currentlyEditing.type === CASH" v-model="currentlyEditing.bigblind" label="Big Blind" type="number" step=".01" />
          <v-text-field v-if="currentlyEditing.type === TOURNAMENT" v-model="currentlyEditing.buyin" label="Entry Fee" type="number" step=".01" />
          <v-text-field v-if="currentlyEditing.type === TOURNAMENT" v-model="currentlyEditing.winnings" label="Winnings" type="number" step=".01" />
          <v-text-field v-if="currentlyEditing.type === TOURNAMENT" v-model="currentlyEditing.players" label="Players" type="number" step=".01" />
          <v-text-field v-if="currentlyEditing.type === TOURNAMENT" v-model="currentlyEditing.finishedin" label="Finished In" type="number" step=".01" />
          <div style="display: flex; gap: 8px;">
            <v-text-field v-model.number="currentlyEditing.editHours" label="Length (hours)" type="number" min="0" style="flex: 1;" />
            <v-text-field v-model.number="currentlyEditing.editMinutes" label="Minutes" type="number" min="0" max="59" style="flex: 1;" />
          </div>
        </div>
        <div style="flex: 1 1 100%; min-width: 200px;">
          <v-text-field v-model="currentlyEditing.notes" label="Notes" />
        </div>
      </div>
      <div style="margin-top: 16px; display: flex; gap: 8px;">
        <v-btn color="primary" @click="applyEditToRow(currentlyEditing)"><v-icon>mdi-check</v-icon> Save</v-btn>
        <v-btn color="secondary" @click="cancelEditRow(currentlyEditing)"><v-icon>mdi-close</v-icon> Cancel</v-btn>
        <v-btn color="error" @click="deleteRow(currentlyEditing)"><v-icon>mdi-delete</v-icon> Delete Session</v-btn>
      </div>
      <v-alert v-if="feedback" type="error" variant="tonal" class="mt-3" density="compact">{{ feedback }}</v-alert>
    </div>
    <v-data-table
      :headers="headers"
      :items="filteredSessionListWithoutEditing"
      item-value="id"
      class="elevation-1"
      :items-per-page="5"
      show-expand
    >
      <template v-slot:[`item.actions`]="{ item }">
        <v-btn v-if="!editarow" icon @click="editRow(item)">
          <v-icon>mdi-pencil</v-icon>
        </v-btn>
        <v-btn v-if="!editarow" icon @click="deleteRow(item)">
          <v-icon>mdi-delete</v-icon>
        </v-btn>
        <v-btn v-if="item.editrow" icon @click="applyEditToRow(item)">
          <v-icon>mdi-check</v-icon>
        </v-btn>
        <v-btn v-if="item.editrow" icon @click="cancelEditRow(item)">
          <v-icon>mdi-close</v-icon>
        </v-btn>
      </template>

      <template v-slot:[`item.sessiondate`]="{ item }">
        <v-menu
          v-if="item.editrow"
          v-model="item.menu"
          :close-on-content-click="false"
          :nudge-right="40"
          transition="scale-transition"
          offset-y
          min-width="auto"
        >
          <template v-slot:activator="{ attrs }">
            <v-text-field
              v-model="item.sessiondate"
              label="Session Date"
              prepend-icon="mdi-calendar"
              readonly
              v-bind="attrs"
            ></v-text-field>
          </template>
          <v-date-picker v-model="item.sessiondate" @input="item.menu = false"></v-date-picker>
        </v-menu>
        <span v-else>{{ formatDate(item.sessiondate) }}</span>
      </template>

      <template v-slot:[`item.place`]="{ item }">
        <v-select
          v-if="item.editrow"
          v-model="item.place"
          :items="placelist"
          label="Place"
        ></v-select>
        <span v-else>{{ item.place }}</span>
      </template>

      <template v-slot:[`item.game`]="{ item }">
        <v-select
          v-if="item.editrow"
          v-model="item.game"
          :items="gameslist"
          label="Game"
        ></v-select>
        <span v-else>{{ item.game }}</span>
      </template>

      <template v-slot:[`item.bettype`]="{ item }">
        <v-select
          v-if="item.editrow"
          v-model="item.bettype"
          :items="BET_TYPE_LIST_PICKER"
          label="Bet Type"
        ></v-select>
        <span v-else>{{ item.bettype }}</span>
      </template>

      <template v-slot:[`item.net`]="{ item }">
        <v-text-field
          v-if="item.editrow && item.type === CASH"
          v-model="item.net"
          label="Net"
          type="number"
          step=".01"
        ></v-text-field>
        <span v-else>{{ item.net }}</span>
      </template>

      <template v-slot:[`item.length`]="{ item }">
        <span>{{ item.length != null ? lengthToText(item.length) : '' }}</span>
      </template>

      <template v-slot:[`item.notes`]="{ item }">
        <v-text-field
          v-if="item.editrow"
          v-model="item.notes"
          label="Notes"
        ></v-text-field>
        <span v-else>{{ item.notes }}</span>
      </template>

      <template v-slot:[`item.bigblind`]="{ item }">
        <v-text-field
          v-if="item.editrow && item.type === CASH"
          v-model="item.bigblind"
          label="Big Blind"
          type="number"
          step=".01"
        ></v-text-field>
        <span v-else>{{ item.bigblind }}</span>
      </template>

      <template v-slot:[`item.winnings`]="{ item }">
        <v-text-field
          v-if="item.editrow && item.type === TOURNAMENT"
          v-model="item.winnings"
          label="Winnings"
          type="number"
          step=".01"
        ></v-text-field>
        <span v-else>{{ item.winnings }}</span>
      </template>

      <template v-slot:[`item.entryfee`]="{ item }">
        <v-text-field
          v-if="item.editrow && item.type === TOURNAMENT"
          v-model="item.buyin"
          label="Entry Fee"
          type="number"
          step=".01"
        ></v-text-field>
        <span v-else>{{ item.buyin }}</span>
      </template>

      <template v-slot:[`item.players`]="{ item }">
        <v-text-field
          v-if="item.editrow && item.type === TOURNAMENT"
          v-model="item.players"
          label="Players"
          type="number"
          step=".01"
        ></v-text-field>
        <span v-else>{{ item.players }}</span>
      </template>

      <template v-slot:[`item.finishedin`]="{ item }">
        <v-text-field
          v-if="item.editrow && item.type === TOURNAMENT"
          v-model="item.finishedin"
          label="Finished In"
          type="number"
          step=".01"
        ></v-text-field>
        <span v-else>{{ item.finishedin }}</span>
      </template>

      <template v-slot:expanded="{ item }">
        <div v-if="item.editrow" style="padding: 16px; background: #fafafa;">
          <div style="display: flex; flex-wrap: wrap; gap: 16px;">
            <div style="flex: 1 1 300px; min-width: 200px;">
              <v-text-field v-model="item.sessiondate" label="Session Date" type="date" />
              <v-select v-model="item.place" :items="placelist" label="Place" />
              <v-select v-model="item.game" :items="gameslist" label="Game" />
              <v-select v-model="item.bettype" :items="BET_TYPE_LIST_PICKER" label="Bet Type" />
            </div>
            <div style="flex: 1 1 300px; min-width: 200px;">
              <v-text-field v-if="item.type === CASH" v-model="item.net" label="Net" type="number" step=".01" />
              <v-text-field v-if="item.type === CASH" v-model="item.bigblind" label="Big Blind" type="number" step=".01" />
              <v-text-field v-if="item.type === TOURNAMENT" v-model="item.buyin" label="Entry Fee" type="number" step=".01" />
              <v-text-field v-if="item.type === TOURNAMENT" v-model="item.winnings" label="Winnings" type="number" step=".01" />
              <v-text-field v-if="item.type === TOURNAMENT" v-model="item.players" label="Players" type="number" step=".01" />
              <v-text-field v-if="item.type === TOURNAMENT" v-model="item.finishedin" label="Finished In" type="number" step=".01" />
              <div style="display: flex; gap: 8px;">
                <v-text-field v-model.number="item.editHours" label="Length (hours)" type="number" min="0" style="flex: 1;" />
                <v-text-field v-model.number="item.editMinutes" label="Minutes" type="number" min="0" max="59" style="flex: 1;" />
              </div>
            </div>
            <div style="flex: 1 1 100%; min-width: 200px;">
              <v-text-field v-model="item.notes" label="Notes" />
            </div>
          </div>
        </div>
      </template>
    </v-data-table>
  </v-container>
</template>

<script setup>
import { ref, computed } from 'vue';
import { useUserStore } from '@/stores/users';
import { usePlaceStore } from '@/stores/places';
import { useGameStore } from '@/stores/games';
import { useSessionStore } from '@/stores/sessions';
import { updateDropDown } from '@/api/lists.js';
import { lengthToText } from '@/utils/globalFunctions';
import { BET_TYPE_LIST_PICKER, CASH, GAMES_DB, PLACES_DB, TOURNAMENT } from '@/constants/constants';

const props = defineProps(['filters']);

//const selectmode = ref('single');
const feedback = ref(null);
const editarow = ref(false);
const headers = [
  { title: 'Actions', key: 'actions', sortable: false },
  { title: 'Cash or Tour.', key: 'type', sortable: true },
  { title: 'Date Played', key: 'sessiondate', sortable: true },
  { title: 'Place Played', key: 'place', sortable: true },
  { title: 'Game Played', key: 'game', sortable: true },
  { title: 'Bet Type', key: 'bettype', sortable: true },
  { title: 'Net Amount Won', key: 'net', sortable: true },
  { title: 'Length', key: 'length', sortable: true },
  { title: 'Notes', key: 'notes', sortable: false },
  { title: 'Big Blind', key: 'bigblind', sortable: true },
  { title: 'Entry Fee', key: 'entryfee', sortable: true },
  { title: 'Winnings', key: 'winnings', sortable: true },
  { title: 'Players Entered', key: 'players', sortable: true },
  { title: 'Place Finished', key: 'finishedin', sortable: true }
];

const userStore = useUserStore();
const placeStore = usePlaceStore();
const gameStore = useGameStore();
const sessionStore = useSessionStore();
const user = computed(() => userStore.user);
const placelist = computed(() => placeStore.placelist);
const gameslist = computed(() => gameStore.gameslist);

const currentlyEditing = ref(null);

const filteredsessionlist = computed(() => {
  // Defensive: if filters or required fields are missing, return all sessions
  if (
    !props.filters ||
    !props.filters.fromDate ||
    !props.filters.toDate ||
    !props.filters.fromDate.date ||
    !props.filters.toDate.date
  ) {
    return sessionStore.sessionlist;
  }

  return sessionStore.sessionlist.filter(session =>
    (session.sessiondate >= props.filters.fromDate.date &&
      session.sessiondate.setHours(0, 0, 0, 0) <= props.filters.toDate.date.setHours(0, 0, 0, 0) &&
      (!props.filters.gamefilter?.length || props.filters.gamefilter.includes(session.game)) &&
      (!props.filters.typefilter || props.filters.typefilter === session.type) &&
      (!props.filters.placefilter?.length || props.filters.placefilter.includes(session.place)) &&
      (!props.filters.bettypefilter?.length || props.filters.bettypefilter.includes(session.bettype)))
  );
});

const filteredSessionListWithoutEditing = computed(() => {
  if (!currentlyEditing.value) return filteredsessionlist.value;
  return filteredsessionlist.value.filter(session => session.id !== currentlyEditing.value.id);
});

function editRow(item) {
  const totalMinutes = Math.round((item.length || 0) * 60);
  item.editHours = Math.floor(totalMinutes / 60);
  item.editMinutes = totalMinutes % 60;
  item.editrow = true;
  editarow.value = true;
  currentlyEditing.value = item;
}

function cancelEditRow(item) {
  item.editrow = false;
  editarow.value = false;
  currentlyEditing.value = null;
}

function deleteRow(item) {
  item.editrow = false;
  editarow.value = false;
  sessionStore.deleteSession(item);
}

function applyEditToRow(item) {
  feedback.value = null;
  let newrecord = {};

  Promise.all([
    updateDropDown(item.game, GAMES_DB, user.value?.user_id),
    updateDropDown(item.place, PLACES_DB, user.value?.user_id)
  ])
    .then(([rawGame, rawPlace]) => {
      // Fall back to the item's existing value if updateDropDown returned null
      // (handles sessions where game/place was not previously set in the dropdown collection)
      const gamechoice = rawGame ?? item.game ?? null;
      const placechoice = rawPlace ?? item.place ?? null;
      if (!((item.sessiondate === null) || (gamechoice === null) || (placechoice === null) || (item.net === null))) {
        const editedLength = (Number(item.editHours) || 0) + (Number(item.editMinutes) || 0) / 60;
        if (item.type === TOURNAMENT) {
          newrecord = {
            id: item.id,
            from: item.from,
            sessiondate: item.sessiondate,
            place: placechoice,
            type: item.type,
            game: gamechoice,
            notes: item.notes,
            bettype: item.bettype,
            length: editedLength,
            buyin: Number(item.buyin),
            players: Number(item.players),
            finishedin: Number(item.finishedin),
            winnings: Number(item.winnings),
            net: Number(Number(item.winnings) - Number(item.buyin))
          };
        } else {
          newrecord = {
            id: item.id,
            from: item.from,
            sessiondate: item.sessiondate,
            place: placechoice,
            type: item.type,
            game: gamechoice,
            notes: item.notes,
            bettype: item.bettype,
            length: editedLength,
            bigblind: Number(item.bigblind),
            net: Number(item.net)
          };
        }
        feedback.value = "Session Entered!";
        item.editrow = false;
        editarow.value = false;
        currentlyEditing.value = null;
        sessionStore.editSession(newrecord);
      } else {
        feedback.value = "Enter all required information (date, game, place, and net are required).";
      }
    })
    .catch(err => {
      console.error('Error saving session:', err);
      feedback.value = "Error saving session. Please try again.";
    });
}

function formatDate(date) {
  if (!date) return '';
  const d = new Date(date);
  if (isNaN(d)) return '';
  // Use local date methods to be consistent with the date picker
  const day = d.getDate().toString().padStart(2, '0');
  const month = d.toLocaleString('en-US', { month: 'short' });
  const year = d.getFullYear();
  return `${day} ${month}, ${year}`;
}

function getDateInputValue(date) {
  if (!date) return '';
  const d = new Date(date);
  if (isNaN(d)) return '';
  // Use local date methods to match what the date picker expects
  const year = d.getFullYear();
  const month = String(d.getMonth() + 1).padStart(2, '0');
  const day = String(d.getDate()).padStart(2, '0');
  return `${year}-${month}-${day}`;
}

function updateSessionDate(dateString) {
  if (!dateString) {
    currentlyEditing.value.sessiondate = null;
    return;
  }
  // The date picker returns a local date string (YYYY-MM-DD)
  // We need to create a date that represents the same calendar date
  // without timezone conversion issues
  const [year, month, day] = dateString.split('-').map(Number);
  // Create a date at midnight local time
  const localDate = new Date(year, month - 1, day, 0, 0, 0);
  currentlyEditing.value.sessiondate = localDate;
}
</script>

<style>

</style>